/**
 * The persistent time readout (spec 35).
 *
 * Calm text above the final minute ("12 minutes left"), a real MM:SS countdown inside it. One
 * prominent element, no shaking and no sound — urgency, not alarm.
 */
import React from 'react';
import { Clock } from 'lucide-react';

import { formatRemaining } from './surveyTimer';
import { UI_TEXT } from '../../config/ui-text';

const TIMER_TEXT = UI_TEXT.HUMAN_SURVEY.SESSION.TIMER;

interface SurveyCountdownProps {
  remaining: number | null;
  isFinal: boolean;
  paused: boolean;
}

const SurveyCountdown: React.FC<SurveyCountdownProps> = ({ remaining, isFinal, paused }) => {
  if (remaining === null) return null;

  const tone = isFinal ? 'text-red-600' : 'text-gray-600';

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-200">
      <span className={`flex items-center gap-2 text-sm font-semibold ${tone}`}>
        <Clock className="w-4 h-4" aria-hidden="true" />
        {paused
          ? TIMER_TEXT.PAUSED
          : isFinal
            ? TIMER_TEXT.FINAL_MINUTE
            : formatRemaining(remaining)}
      </span>

      {/* aria-live is off deliberately: announcing every second makes the survey unusable with a
          screen reader. The nudges carry role="status" and announce once each instead. */}
      {isFinal && !paused && (
        <span
          className="tabular-nums text-2xl font-bold text-red-600"
          role="timer"
          aria-live="off"
          aria-label={TIMER_TEXT.SECONDS_REMAINING(remaining)}
        >
          {formatRemaining(remaining)}
        </span>
      )}
    </div>
  );
};

export default SurveyCountdown;
